import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { panelResumeGet } from '@/lib/otcApi';
import { CompanyResumeView } from '@/components/CompanyResumeView';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, XCircle } from 'lucide-react';
import { fmtDateTimeIST } from '@/lib/format';

function FullScreen({ children, className = '' }) {
  return (
    <div className={`min-h-screen w-full bg-secondary/30 p-4 md:p-8 ${className}`}>
      <div className="mx-auto max-w-4xl">{children}</div>
    </div>
  );
}

function Notice({ icon: Icon, title, body }) {
  return (
    <Card className="card-elevated max-w-lg mx-auto">
      <CardContent className="p-7 text-center">
        <Icon className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}

export default function PanelResume() {
  const { token } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['panel-resume', token],
    queryFn: () => panelResumeGet(token),
    retry: false,
  });

  if (isLoading) {
    return (
      <FullScreen>
        <p className="text-center text-sm text-muted-foreground">Loading candidate profile…</p>
      </FullScreen>
    );
  }

  if (!data || data.state === 'invalid') {
    return (
      <FullScreen className="flex items-center justify-center">
        <Notice
          icon={XCircle}
          title="Link not available"
          body="This candidate profile link is invalid or has expired. Contact HR if you need access."
        />
      </FullScreen>
    );
  }

  if (data.state === 'no_resume') {
    return (
      <FullScreen className="flex items-center justify-center">
        <Notice
          icon={FileText}
          title="Profile not ready yet"
          body={`The standardised CV for ${data.candidate_name || 'this candidate'} has not been generated yet. Please check with HR.`}
        />
      </FullScreen>
    );
  }

  const generatedAt = data.generated_at ? fmtDateTimeIST(data.generated_at) : null;
  const resumePayload = data.resume
    ? { ...data.resume, generated_at: generatedAt || data.resume.generated_at }
    : null;

  return (
    <FullScreen data-testid="panel-resume-page">
      <div className="mb-4" data-testid="panel-resume-toolbar">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
          Kadel Labs · Panellist briefing
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {data.candidate_name}
        </h1>
        <p className="text-sm text-muted-foreground">
          {data.round_label} · {data.role_label}
          {data.panellist_name ? ` · For ${data.panellist_name}` : ''}
        </p>
      </div>

      <p className="mb-4 text-xs text-muted-foreground">
        This is the standardised company profile (not the original upload). For interview
        preparation only.
      </p>

      <div data-testid="panel-resume-profile">
        <CompanyResumeView
          resume={resumePayload}
          template={data.template}
          photoUrl={data.photo_url || null}
        />
      </div>
    </FullScreen>
  );
}
